import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import api from "../lib/api";
import { Plus, Edit2, Trash2, Image as ImageIcon, Package, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { formatApiError } from "../lib/errors";
import { compressImage, uploadImage } from "../lib/image";

function fileToBase64(file) {
    return uploadImage(file, api).catch(() => compressImage(file)).catch(() => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    });
}

const empty = {
    name: "",
    description: "",
    images: [],
    items: [],
    sizes: [],
    conditions: [],
    final_price: "",
    order_url: "",
    order_button_label: "Order Now",
    order: 0,
    active: true,
};

const inputCls = "w-full bg-black border border-meraki-gold/20 px-4 py-3 text-meraki-text focus:outline-none focus:border-meraki-gold";

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-meraki-muted mb-2">{label}</label>
            {children}
            {hint && <div className="text-[10px] text-meraki-muted/70 mt-1.5">{hint}</div>}
        </div>
    );
}

const NameCountEditor = forwardRef(function NameCountEditor(
    { value, onChange, namePlaceholder, countPlaceholder, testidPrefix, nameLabel = "Name" },
    ref
) {
    const [draftName, setDraftName] = useState("");
    const [draftCount, setDraftCount] = useState("");
    const keyName = nameLabel === "Size" ? "label" : "name";

    const commit = (rawName = draftName, rawCount = draftCount) => {
        const n = (rawName || "").trim();
        if (!n) return false;
        const count = rawCount === "" ? null : Math.max(0, parseInt(rawCount, 10) || 0);
        const newItem = keyName === "label" ? { label: n, count } : { name: n, count };
        onChange([...(value || []), newItem]);
        setDraftName("");
        setDraftCount("");
        return true;
    };

    useImperativeHandle(ref, () => ({
        flushDraft: () => commit(),
    }), [draftName, draftCount, value]);

    const onKey = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            commit();
        }
    };

    const list = value || [];
    const updateCount = (i, raw) => {
        const count = raw === "" ? null : Math.max(0, parseInt(raw, 10) || 0);
        onChange(list.map((item, j) => (j === i ? { ...item, count } : item)));
    };
    return (
        <div className="space-y-3">
            <div className="flex gap-2">
                <input
                    data-testid={`${testidPrefix}-name`}
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    onKeyDown={onKey}
                    placeholder={namePlaceholder}
                    className={`${inputCls} flex-1`}
                />
                <input
                    data-testid={`${testidPrefix}-count`}
                    type="number"
                    min="0"
                    value={draftCount}
                    onChange={(e) => setDraftCount(e.target.value)}
                    onKeyDown={onKey}
                    placeholder={countPlaceholder}
                    className={`${inputCls} w-20`}
                />
                <button
                    type="button"
                    onClick={() => commit()}
                    data-testid={`${testidPrefix}-add`}
                    className="inline-flex items-center gap-2 px-5 py-2 bg-meraki-gold text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-meraki-gold-hover transition-colors whitespace-nowrap"
                >
                    <Plus className="w-3.5 h-3.5" /> Add
                </button>
            </div>
            <div className="text-[10px] text-meraki-muted/70">
                Type a {nameLabel.toLowerCase()} + optional count, then press <span className="text-meraki-gold">Enter</span> or click <span className="text-meraki-gold">Add</span>.
            </div>
            {list.length > 0 ? (
                <ul className="space-y-1.5">
                    {list.map((v, i) => (
                        <li key={`${v[keyName] ?? ""}-${i}`} className="flex items-center justify-between gap-3 bg-black/50 border border-meraki-gold/15 px-3 py-2">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                <span className="text-sm text-meraki-text truncate">{v[keyName]}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <input
                                    type="number"
                                    min="0"
                                    value={v.count ?? ""}
                                    onChange={(e) => updateCount(i, e.target.value)}
                                    placeholder={countPlaceholder}
                                    data-testid={`${testidPrefix}-edit-count-${i}`}
                                    className="w-20 bg-black border border-meraki-gold/20 px-2 py-1.5 text-sm text-meraki-text focus:outline-none focus:border-meraki-gold text-center"
                                />
                                <button
                                    type="button"
                                    onClick={() => onChange(list.filter((_, j) => j !== i))}
                                    className="text-meraki-muted hover:text-red-400 transition-colors"
                                    aria-label="Remove"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="text-[10px] uppercase tracking-[0.25em] text-meraki-muted/60 border border-dashed border-meraki-gold/15 px-3 py-3">
                    No {nameLabel.toLowerCase()}s added yet.
                </div>
            )}
        </div>
    );
});

const ListEditor = forwardRef(function ListEditor(
    { value, onChange, placeholder, testid },
    ref
) {
    const [draft, setDraft] = useState("");

    const commit = (raw = draft) => {
        const v = (raw || "").trim();
        if (!v) return false;
        onChange([...(value || []), v]);
        setDraft("");
        return true;
    };

    useImperativeHandle(ref, () => ({
        flushDraft: () => commit(),
    }), [draft, value]);

    const onKey = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            commit();
        }
    };

    const list = value || [];
    return (
        <div className="space-y-3">
            <div className="flex gap-2">
                <input
                    data-testid={testid}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKey}
                    placeholder={placeholder}
                    className={`${inputCls} flex-1`}
                />
                <button
                    type="button"
                    onClick={() => commit()}
                    data-testid={`${testid}-add`}
                    className="inline-flex items-center gap-2 px-5 py-2 bg-meraki-gold text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-meraki-gold-hover transition-colors whitespace-nowrap"
                >
                    <Plus className="w-3.5 h-3.5" /> Add
                </button>
            </div>
            <div className="text-[10px] text-meraki-muted/70">
                Type a condition, then press <span className="text-meraki-gold">Enter</span> or click <span className="text-meraki-gold">Add</span>.
            </div>
            {list.length > 0 ? (
                <ul className="space-y-1.5">
                    {list.map((v, i) => (
                        <li key={`${v}-${i}`} className="flex items-center justify-between gap-3 bg-black/50 border border-meraki-gold/15 px-3 py-2">
                            <span className="text-sm text-meraki-text">{v}</span>
                            <button
                                type="button"
                                onClick={() => onChange(list.filter((_, j) => j !== i))}
                                className="text-meraki-muted hover:text-red-400 transition-colors"
                                aria-label="Remove"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="text-[10px] uppercase tracking-[0.25em] text-meraki-muted/60 border border-dashed border-meraki-gold/15 px-3 py-3">
                    Nothing added yet.
                </div>
            )}
        </div>
    );
});

function ImagesEditor({ value, onChange, testid }) {
    const inputRef = useRef(null);
    const images = value || [];
    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Original file is too large. Pick something under 5MB.");
            return;
        }
        if (images.length >= 3) {
            toast.error("Max 3 images per article.");
            return;
        }
        try {
            const b64 = await fileToBase64(file);
            onChange([...images, b64]);
        } catch {
            toast.error("Couldn't process this image. Try a different file.");
        }
        if (inputRef.current) inputRef.current.value = "";
    };
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="relative aspect-[4/5] bg-black border border-meraki-gold/20 overflow-hidden">
                        {images[i] ? (
                            <>
                                <img src={images[i]} alt={`Image ${i + 1}`} className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => onChange(images.filter((_, j) => j !== i))}
                                    className="absolute top-1 right-1 bg-black/80 border border-meraki-gold/40 p-1 text-meraki-gold hover:text-red-400 hover:border-red-400 transition-colors"
                                    aria-label="Remove image"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                                <div className="absolute bottom-1 left-1 text-[9px] uppercase tracking-[0.25em] text-meraki-bg bg-meraki-gold px-1.5 py-0.5">
                                    {i + 1}
                                </div>
                            </>
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-meraki-muted">
                                <ImageIcon className="w-6 h-6" />
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {images.length < 3 && (
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    onChange={onFile}
                    data-testid={testid}
                    className="text-xs text-meraki-muted file:mr-4 file:py-2 file:px-4 file:border file:border-meraki-gold/30 file:bg-transparent file:text-meraki-gold file:uppercase file:tracking-wider file:text-[10px] hover:file:bg-meraki-gold/10"
                />
            )}
        </div>
    );
}

function ArticleForm({ initial, onCancel, onSaved }) {
    const [form, setForm] = useState(initial || empty);
    const [saving, setSaving] = useState(false);
    const itemsRef = useRef(null);
    const sizesRef = useRef(null);
    const conditionsRef = useRef(null);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        // Flush any pending draft input the user typed but forgot to click "Add"
        itemsRef.current?.flushDraft?.();
        sizesRef.current?.flushDraft?.();
        conditionsRef.current?.flushDraft?.();
        // Yield one tick so the flushed setState calls above land before we read `form`
        await new Promise((r) => setTimeout(r, 0));
        setSaving(true);
        try {
            // Read latest form via functional updater to avoid the stale closure
            const latest = await new Promise((resolve) =>
                setForm((f) => { resolve(f); return f; })
            );
            if (latest.id) {
                const { id, created_at, ...payload } = latest;
                await api.put(`/articles/${id}`, payload);
                toast.success("Article updated");
            } else {
                await api.post("/articles", latest);
                toast.success("Article added");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="bg-meraki-surface border border-meraki-gold/20 p-6 md:p-8 space-y-6" data-testid="article-form">
            <h3 className="font-heading uppercase tracking-[0.25em] text-meraki-gold text-sm">
                {form.id ? "Edit article" : "Add new article"}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Name">
                    <input data-testid="article-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="e.g. Meraki Tee Vol.1" />
                </Field>
                <Field label="Final Price" hint="Free text — include currency symbol if you want (e.g. ₹1,499)">
                    <input data-testid="article-price" value={form.final_price || ""} onChange={(e) => set("final_price", e.target.value)} className={inputCls} placeholder="₹1,499" />
                </Field>
            </div>

            <Field label="Description" hint="2–4 lines work best">
                <textarea data-testid="article-description" rows={3} value={form.description || ""} onChange={(e) => set("description", e.target.value)} className={inputCls} />
            </Field>

            <Field label="Images (max 3)" hint="Each image up to 2MB">
                <ImagesEditor value={form.images} onChange={(v) => set("images", v)} testid="article-image-upload" />
            </Field>

            <Field label="What's Included (items list)" hint="Add bullets one at a time. Count is optional.">
                <NameCountEditor
                    ref={itemsRef}
                    value={form.items}
                    onChange={(v) => set("items", v)}
                    namePlaceholder="e.g. 100% organic cotton tee"
                    countPlaceholder="Qty"
                    testidPrefix="article-item-input"
                />
            </Field>

            <Field label="Sizes Available (optional)" hint='Add sizes like "S", "M", "L", "42" with optional stock count.'>
                <NameCountEditor
                    ref={sizesRef}
                    value={form.sizes}
                    onChange={(v) => set("sizes", v)}
                    namePlaceholder="e.g. M / L / 42"
                    countPlaceholder="Stock"
                    testidPrefix="article-size-input"
                    nameLabel="Size"
                />
            </Field>

            <Field label="Conditions" hint='e.g. "Delivery Included", "GST 18%"'>
                <ListEditor ref={conditionsRef} value={form.conditions} onChange={(v) => set("conditions", v)} placeholder="e.g. Delivery Included" testid="article-condition-input" />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Order URL" hint="Where the 'Order Now' button takes the user (leave empty to show 'Coming Soon')">
                    <input data-testid="article-order-url" type="url" value={form.order_url || ""} onChange={(e) => set("order_url", e.target.value)} className={inputCls} placeholder="https://forms.gle/…" />
                </Field>
                <Field label="Order Button Label">
                    <input data-testid="article-order-label" value={form.order_button_label || ""} onChange={(e) => set("order_button_label", e.target.value)} className={inputCls} placeholder="Order Now" />
                </Field>
                <Field label="Display Order" hint="Lower number = shown first">
                    <input data-testid="article-order" type="number" value={form.order ?? 0} onChange={(e) => set("order", parseInt(e.target.value || "0", 10))} className={inputCls} />
                </Field>
                <Field label="Visibility">
                    <select data-testid="article-active" value={form.active ? "true" : "false"} onChange={(e) => set("active", e.target.value === "true")} className={inputCls}>
                        <option value="true">Active (visible on site)</option>
                        <option value="false">Hidden</option>
                    </select>
                </Field>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-meraki-gold/15">
                <button data-testid="article-save-btn" type="submit" disabled={saving} className="px-6 py-3 bg-meraki-gold text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-meraki-gold-hover transition-colors disabled:opacity-50">
                    {saving ? "Saving…" : form.id ? "Update Article" : "Add Article"}
                </button>
                <button type="button" onClick={onCancel} className="px-6 py-3 border border-meraki-gold/30 text-meraki-text text-xs uppercase tracking-[0.25em] hover:border-meraki-gold transition-colors">
                    Cancel
                </button>
            </div>
        </form>
    );
}

export default function AdminArticles() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/articles?include_inactive=true");
            setArticles(data || []);
        } catch {
            toast.error("Failed to load articles");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onDelete = async (id) => {
        if (!window.confirm("Delete this article? This cannot be undone.")) return;
        try {
            await api.delete(`/articles/${id}`);
            toast.success("Article removed");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.4em] text-meraki-gold mb-3">Meraki Merchandise</div>
                    <h1 className="font-display text-4xl md:text-5xl uppercase text-meraki-text leading-tight">Manage Articles</h1>
                    <p className="text-meraki-muted text-sm mt-3 max-w-xl">Add merch drops, change prices, swap images and order links anytime.</p>
                </div>
                <button data-testid="add-article-btn" onClick={() => setEditing("new")} className="flex items-center gap-2 px-6 py-3 bg-meraki-gold text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-meraki-gold-hover transition-colors">
                    <Plus className="w-4 h-4" /> Add Article
                </button>
            </div>

            {editing && (
                <div className="mb-12">
                    <ArticleForm
                        initial={editing === "new" ? null : editing}
                        onCancel={() => setEditing(null)}
                        onSaved={() => { setEditing(null); load(); }}
                    />
                </div>
            )}

            {loading ? (
                <div className="text-meraki-muted text-sm">Loading…</div>
            ) : articles.length === 0 ? (
                <div className="border border-dashed border-meraki-gold/20 p-12 text-center">
                    <Package className="w-12 h-12 text-meraki-gold/40 mx-auto mb-4" />
                    <div className="text-meraki-muted text-sm">No articles yet. Click <b className="text-meraki-text">Add Article</b> to launch your first drop.</div>
                </div>
            ) : (
                <div className="border border-meraki-gold/20 overflow-x-auto">
                    <table className="w-full" data-testid="articles-table">
                        <thead className="bg-meraki-surface">
                            <tr className="text-[10px] uppercase tracking-[0.25em] text-meraki-muted">
                                <th className="text-left p-4 w-16">Image</th>
                                <th className="text-left p-4">Name</th>
                                <th className="text-left p-4 hidden md:table-cell">Price</th>
                                <th className="text-left p-4 hidden lg:table-cell">Order URL</th>
                                <th className="text-left p-4 w-24">Status</th>
                                <th className="text-left p-4 w-16">Order</th>
                                <th className="text-right p-4 w-32">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {articles.map((a) => (
                                <tr key={a.id} className="border-t border-meraki-gold/10 hover:bg-meraki-surface/50">
                                    <td className="p-4">
                                        <div className="w-12 h-12 bg-black border border-meraki-gold/20 overflow-hidden flex items-center justify-center">
                                            {a.images && a.images[0] ? <img src={a.images[0]} alt="" className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-meraki-muted" />}
                                        </div>
                                    </td>
                                    <td className="p-4 text-meraki-text">{a.name}</td>
                                    <td className="p-4 text-meraki-gold hidden md:table-cell font-semibold">{a.final_price || "—"}</td>
                                    <td className="p-4 text-meraki-muted hidden lg:table-cell text-xs">
                                        {a.order_url ? (
                                            <a href={a.order_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-meraki-gold transition-colors">
                                                <ExternalLink className="w-3 h-3" /> Link
                                            </a>
                                        ) : "—"}
                                    </td>
                                    <td className="p-4">
                                        <span className={`text-[10px] uppercase tracking-[0.25em] px-2 py-1 ${a.active ? "text-meraki-gold border border-meraki-gold/40" : "text-meraki-muted border border-meraki-muted/30"}`}>
                                            {a.active ? "Active" : "Hidden"}
                                        </span>
                                    </td>
                                    <td className="p-4 text-meraki-muted">{a.order ?? 0}</td>
                                    <td className="p-4 text-right">
                                        <div className="inline-flex gap-2">
                                            <button data-testid={`edit-article-${a.id}`} onClick={() => setEditing(a)} className="p-2 border border-meraki-gold/30 hover:border-meraki-gold text-meraki-gold transition-colors">
                                                <Edit2 className="w-3 h-3" />
                                            </button>
                                            <button data-testid={`delete-article-${a.id}`} onClick={() => onDelete(a.id)} className="p-2 border border-red-900/40 hover:border-red-700 text-red-400 transition-colors">
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </AdminLayout>
    );
}
